import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from 'js-cookie';
import COMMON_CONSTANTS from '../constants/commonConstants';
import {
    isValidJWT,
    checkSessionIsAboutToExpire,
    checkIsTokenExpired,
    checkIsAuthorized,
    updatePortalSessions
} from '../utils/authUtil';

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [sessionIsAboutToExpire, setSessionIsAboutToExpire] = useState(false);
    const [isTokenExpired, setIsTokenExpired] = useState(false);
    const [expireTime, setExpireTime] = useState(null);

    const checkAuthUser = async () => {
        const userSessionDecoded = await isValidJWT(Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME));

        if (userSessionDecoded) {
            const aboutToExpire = checkSessionIsAboutToExpire(userSessionDecoded);
            const tokenExpired = checkIsTokenExpired(userSessionDecoded);
            const authorized = checkIsAuthorized(userSessionDecoded);

            setSessionIsAboutToExpire(aboutToExpire);
            setIsTokenExpired(tokenExpired);
            setIsAuthorized(authorized);
            setExpireTime(new Date(userSessionDecoded.exp));
            updatePortalSessions();
        } else {
            setSessionIsAboutToExpire(false);
            setIsTokenExpired(true);
            setIsAuthorized(false);
            setExpireTime(null);
            // Redirect to signout page
            const currentPathName = window.location.pathname?.replace(/\/+$/, '');
             if (COMMON_CONSTANTS.AUTHENTICATED_PAGES.includes(currentPathName)) {
                 const currentPath = currentPathName + window.location.search;
                 const signOutPath = COMMON_CONSTANTS.SIGN_OUT_URL;
                if (currentPath !== signOutPath) {  // Prevent infinite redirect loop if already on signout page
                    window.location.href = signOutPath;
                }
            }
 
        }
    }

    // Initial check when component mounts
    useEffect(() => {
        checkAuthUser();
    }, []);

    // Set up interval to check session status every 2 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            checkAuthUser();
        }, 3 * 60000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    const signout = () => {
        if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME) && Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME).length > 0) {
            //Clearing all the cookies and session storage
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME);
            Cookies.remove(COMMON_CONSTANTS.AUTH_SESSIONS.SOB_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME);
            sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_SELECTED_POLICY_COOKIE_NAME);
            setIsAuthorized(false);
            setSessionIsAboutToExpire(false);
            setIsTokenExpired(true);
            setExpireTime(null);
            window.location.href = COMMON_CONSTANTS.SIGN_OUT_URL;
        } else {
            window.location.href = COMMON_CONSTANTS.SIGN_IN_URL;
        }
    }

    /**
     * This function calls /ccmc-website-services/validate-token service of NodeJs to validate accessToken and check it's exp time. 
     * If we need to extend token then it will extend the tokens.
     * @returns {Promise<void>}
     */
    const validateAndExtendToken = async () => {
        const url = COMMON_CONSTANTS.VALIDATE_TOKEN_ENDPOINT;
        try {
            const response = await fetch(url);
            const json = await response.json();

            if (json?.result === "Unauthorized") {
                signout();
            } else if (json?.result === "Extended") {
                checkAuthUser();
            }
        } catch (error) {
            signout();
        }
    }

    return (
        <AuthContext.Provider value={{
            isAuthorized,
            signout,
            sessionIsAboutToExpire,
            isTokenExpired,
            setSessionIsAboutToExpire,
            expireTime,
            checkAuthUser,
            validateAndExtendToken,
            isValidJWT
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => useContext(AuthContext);
export { AuthContext };
